'use client'

import { useState } from 'react'
import { motion } from 'framer-motion'
import { Users, Search, Target, Award, CheckCircle, Clock, Globe, TrendingUp, UserCheck, Building, Briefcase, Star, Handshake, Calendar } from 'lucide-react'
import HiringRequestModal from '@/components/modals/HiringRequestModal'
import GuideDownloadModal from '@/components/modals/GuideDownloadModal'
import JobSeekerModal from '@/components/modals/JobSeekerModal'
import DynamicCompanyInfo from '@/components/DynamicCompanyInfo'

export default function RecruitmentPage() {
  const [showHiringModal, setShowHiringModal] = useState(false)
  const [showGuideModal, setShowGuideModal] = useState(false)
  const [showJobSeekerModal, setShowJobSeekerModal] = useState(false)
  const [selectedServiceType, setSelectedServiceType] = useState('')
  const recruitmentServices = [
    {
      icon: <Search className="w-8 h-8" />,
      title: 'Executive Search',
      description: 'C-level and senior leadership recruitment across all industries with comprehensive cultural fit evaluation and confidential search processes.',
      features: [
        'CEO, CTO, CFO & VP Positions',
        'Board of Directors Placement',
        'Leadership Assessment',
        'Succession Planning'
      ],
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: 'Professional Staffing',
      description: 'Mid-level and senior professional recruitment across finance, marketing, operations, HR, sales, and management positions.',
      features: [
        'Management & Team Leads',
        'Finance & Accounting',
        'Marketing & Sales',
        'Operations & Logistics'
      ],
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Technical Recruitment',
      description: 'Specialized technical talent sourcing for IT, engineering, healthcare, manufacturing, and other technical industries.',
      features: [
        'Software Development',
        'Engineering Roles',
        'Healthcare Professionals',
        'Manufacturing & Quality'
      ],
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: 'Contract & Temporary',
      description: 'Flexible staffing solutions across all sectors including temporary, contract-to-hire, and project-based assignments.',
      features: [
        'Temporary Staffing',
        'Contract-to-Hire',
        'Project Specialists',
        'Seasonal Workforce'
      ],
      color: 'from-orange-500 to-red-500'
    },
    {
      icon: <Handshake className="w-8 h-8" />,
      title: 'Volume Recruitment',
      description: 'Large-scale recruitment campaigns for rapid hiring needs including call centers, retail, hospitality, and manufacturing.',
      features: [
        'Mass Hiring Campaigns',
        'Campus Recruitment',
        'Retail & Hospitality',
        'Customer Service Teams'
      ],
      color: 'from-indigo-500 to-purple-500'
    },
    {
      icon: <Calendar className="w-8 h-8" />,
      title: 'Recruitment Process Outsourcing',
      description: 'Complete end-to-end recruitment process management including employer branding, sourcing, screening, and onboarding.',
      features: [
        'Full-Service RPO',
        'Employer Branding',
        'ATS Management',
        'Onboarding Support'
      ],
      color: 'from-teal-500 to-green-500'
    }
  ]

  const industries = [
    { name: 'Information Technology', icon: <TrendingUp className="w-6 h-6" />, count: '300+ placements' },
    { name: 'Banking & Finance', icon: <UserCheck className="w-6 h-6" />, count: '250+ placements' },
    { name: 'Healthcare & Pharmaceuticals', icon: <Building className="w-6 h-6" />, count: '180+ placements' },
    { name: 'Manufacturing & Engineering', icon: <Globe className="w-6 h-6" />, count: '220+ placements' },
    { name: 'Retail & E-commerce', icon: <Target className="w-6 h-6" />, count: '200+ placements' },
    { name: 'Hospitality & Tourism', icon: <Award className="w-6 h-6" />, count: '150+ placements' },
    { name: 'Education & Training', icon: <Users className="w-6 h-6" />, count: '120+ placements' },
    { name: 'Real Estate & Construction', icon: <Building className="w-6 h-6" />, count: '100+ placements' },
    { name: 'Media & Entertainment', icon: <Star className="w-6 h-6" />, count: '80+ placements' },
    { name: 'Government & Non-Profit', icon: <Handshake className="w-6 h-6" />, count: '90+ placements' }
  ]

  const roles = [
    'C-Level Executives (CEO, CTO, CFO, CMO)',
    'Vice Presidents & Directors',
    'Software Developers & Engineers',
    'Sales & Marketing Professionals', 
    'Finance & Accounting Specialists',
    'Human Resources Managers',
    'Operations & Supply Chain Managers',
    'Healthcare Professionals',
    'Customer Service Representatives',
    'Administrative & Support Staff',
    'Manufacturing & Production Workers',
    'Legal & Compliance Officers',
    'Project & Program Managers',
    'Quality Assurance Specialists',
    'Business Analysts & Consultants',
    'Creative & Design Professionals'
  ]

  const process = [
    {
      step: '01',
      title: 'Requirements Analysis',
      description: 'Comprehensive analysis of role requirements, company culture, ideal candidate profile, and industry-specific needs.',
      icon: <Search className="w-6 h-6" />
    },
    {
      step: '02',
      title: 'Sourcing Strategy',
      description: 'Multi-channel sourcing using our extensive network, job boards, social media, and industry connections.',
      icon: <Users className="w-6 h-6" />
    },
    {
      step: '03',
      title: 'Screening & Assessment',
      description: 'Thorough screening including skills assessment, background verification, and cultural fit evaluation.',
      icon: <Target className="w-6 h-6" />
    },
    {
      step: '04',
      title: 'Client Presentation',
      description: 'Curated candidate shortlist with detailed profiles, interview recommendations, and comprehensive insights.',
      icon: <Briefcase className="w-6 h-6" />
    },
    {
      step: '05',
      title: 'Interview Management',
      description: 'Complete interview coordination, scheduling, feedback collection, and process optimization.',
      icon: <Clock className="w-6 h-6" />
    },
    {
      step: '06',
      title: 'Placement & Follow-up',
      description: 'Offer negotiation, onboarding support, and post-placement follow-up to ensure long-term success.',
      icon: <CheckCircle className="w-6 h-6" />
    }
  ]

  return (
    <main className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-blue-600/20 to-purple-600/20"></div>
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
        </div>

        <div className="relative container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="inline-flex items-center px-4 py-2 bg-blue-600/20 border border-blue-500/30 rounded-full text-blue-300 text-sm font-medium mb-6"
            >
              <Users className="w-4 h-4 mr-2" />
              Complete Recruitment Solutions
            </motion.div>
            
            <h1 className="text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Find the Right Talent
              <span className="block bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Across All Industries
              </span>
            </h1>
            
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              <DynamicCompanyInfo type="companyName">
                {(companyInfo) => (
                  <>
                    {companyInfo?.companyName || 'Our company'} provides comprehensive recruitment services across all industries and job levels. 
                    From entry-level positions to C-suite executives, we connect the right talent with the right opportunities.
                  </>
                )}
              </DynamicCompanyInfo>
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setSelectedServiceType('')
                  setShowHiringModal(true)
                }}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl hover:shadow-xl hover:shadow-blue-500/25 transition-all duration-300 font-semibold"
              >
                Start Hiring Process
              </motion.button>
              
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowJobSeekerModal(true)}
                className="border-2 border-blue-500/50 text-blue-300 px-8 py-4 rounded-xl hover:border-blue-400 hover:bg-blue-600/10 transition-all duration-300 font-semibold"
              >
                Join Talent Network
              </motion.button>
            </div>

            {/* Stats */}
            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-white mb-2">2500+</div>
                <div className="text-gray-400">Total Placements</div>
              </div>
              <div className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-white mb-2">24hrs</div>
                <div className="text-gray-400">Initial Response</div>
              </div>
              <div className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-white mb-2">96%</div>
                <div className="text-gray-400">Client Satisfaction</div>
              </div>
              <div className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-white mb-2">10+</div>
                <div className="text-gray-400">Industries Served</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Recruitment Services */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-block px-4 py-2 bg-blue-100 text-blue-600 rounded-full text-sm font-semibold mb-4">
              RECRUITMENT SERVICES
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Complete <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Hiring Solutions</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              From executive search to volume recruitment, we provide comprehensive hiring solutions 
              across all industries, job levels, and employment types.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {recruitmentServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group relative"
              >
                <div className="relative bg-white p-8 rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-xl transition-all duration-500 h-full">
                  <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity duration-500`}></div>
                  
                  <div className={`w-16 h-16 bg-gradient-to-br ${service.color} rounded-xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    {service.icon}
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                  <p className="text-gray-600 leading-relaxed mb-6">{service.description}</p>
                  
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-900">Specializations:</h4>
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-3 flex-shrink-0" />
                        <span className="text-sm text-gray-600">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-6">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        setSelectedServiceType(service.title.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/_$/, ''))
                        setShowHiringModal(true)
                      }}
                      className="w-full px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all duration-300 text-sm font-medium"
                    >
                      Request {service.title}
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Industries We <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Serve</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our extensive industry knowledge and network spans across all major sectors, 
              ensuring we find candidates who understand your specific business needs.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-6">
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-xl border border-gray-100 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mr-4">
                      {industry.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{industry.name}</h3>
                      <p className="text-sm text-gray-500">{industry.count}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-slate-900 to-slate-800">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready to Find Your Perfect Candidate?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Whether you need one position filled or an entire team built, our comprehensive 
              recruitment services deliver the right talent for your organization.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setSelectedServiceType('')
                  setShowHiringModal(true)
                }}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl font-semibold hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-300"
              >
                Start Recruitment Process
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowGuideModal(true)}
                className="border-2 border-gray-400 text-gray-300 px-8 py-4 rounded-xl hover:border-blue-400 hover:text-blue-400 transition-all duration-300 font-semibold"
              >
                Download Hiring Guide
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Modals */}
      <HiringRequestModal
        isOpen={showHiringModal}
        onClose={() => setShowHiringModal(false)}
        serviceType={selectedServiceType}
      />
      
      <GuideDownloadModal
        isOpen={showGuideModal}
        onClose={() => setShowGuideModal(false)}
      />
      
      <JobSeekerModal
        isOpen={showJobSeekerModal}
        onClose={() => setShowJobSeekerModal(false)}
      />
    </main>
  )
}